import { datadogRum } from '@datadog/browser-rum';
import { Route } from 'react-router';
import type { RouterState, RouteProps } from 'react-router';
import type { FunctionComponent } from 'react';
import { isTrue } from './utils';
import { window } from './globals';
import type { WindowUser } from './globals';

/**
 * Check if DataDog Real User Monitoring (RUM) is enabled.
 * @returns {boolean} - True if DataDog RUM is enabled, false otherwise.
 */
function isDataDogRUMEnabled(): boolean {
  return isTrue(window.datadogRUM?.enabled);
}

/**
 * Sets the user data for DataDog Real User Monitoring (RUM).
 *
 * @param {WindowUser} windowUser - The user data to set.
 */
function setDataDogUser(windowUser: WindowUser): void {
  datadogRum.setUser({
    //  ID must be a string
    id: `${windowUser.id}`,
    name: windowUser.username,
    email: windowUser.email,
    organizationId: windowUser.organizationId,
    organizationTitle: windowUser.organization?.title,
  });
}

/**
 * Turns on DataDog Real User Monitoring (RUM) for the application.
 */
export function enableDataDogRUM(): void {
  if (isDataDogRUMEnabled()) {
    datadogRum.init({
      ...window.datadogRUM,
      version: window.fossa_version,
      //  This tells DataDog to track views manually, which is needed for SPA applications.
      trackViewsManually: true,
    });

    if (window.user) {
      setDataDogUser(window.user);
    }
  }
}

/**
 * This is used for tracking view/route changes in our SPA/ReactRouter application.
 * If DataDogRUM is enabled, this will start a new view with the pathname.
 *
 * @param {RouterProps} routerProps - The router props object.
 */
function trackRouteChange(routerProps: RouterState): void {
  datadogRum.startView(routerProps.location.pathname);
}

/**
 * Drop-in replacement for Route from react-router, which tracks route changes.
 * This is needed because in a SPA application like ours, DD RUM needs doesn't
 * really understand page loading / page changes.
 *
 * @param {RouteProps} props - Replacement
 */
const RouteWithTracking: FunctionComponent<RouteProps> = (props) => (
  <Route
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    //  We don't use onEnter for anything else, so we are safe to "override" it here.
    onEnter={trackRouteChange}
  />
);

/**
 * Returns the correct Route component to use based on whether DataDog RUM is enabled.
 * This avoids us unnecessarily wrapping all our routes in RouteWithTracking, and
 * having prop spreading on every Route component (which could be bad for performance).
 */
export function getRouteComponent(): FunctionComponent<RouteProps> | Route {
  if (isDataDogRUMEnabled()) {
    return RouteWithTracking;
  }
  return Route;
}
